/**
 * Typography helpers（happa v1.1）。
 *
 * 和欧混植（mixed-script）描画のための pure ユーティリティ。spec §5.4.2 /
 * wireframe v0.10 L694–706 と bit 単位で整合する 2 関数のみ提供する。
 *
 * - `isCJK(c)`: 文字が CJK 系かを Unicode 範囲で判定
 * - `buildFontSpec(family, fontSize, weight?)`: Canvas `ctx.font` 互換の
 *   font shorthand を構築
 *
 * 文字測定（`ctx.measureText`）やパス沿い描画は render 層（`src/render/text.ts`）
 * が持つ責務。本モジュールは Canvas / DOM に触れない。
 *
 * 設計参照:
 * - spec §5.4.2（混植、L537–555）
 * - spec Unicode 範囲表（L1093–1098 相当）
 * - wireframe `wireframes/v0.10/wireframe.html` L694–706
 */

/**
 * CJK 系文字判定。`true` なら日本語フォント、`false` なら Latin フォントで
 * 描画するというルーティングスイッチとして使う（spec §5.4.2）。
 *
 * 判定対象の Unicode ブロック（spec §5.4.2 L544–549 / L1093–1098 と一致）:
 *
 * | 範囲 (hex)       | ブロック                    |
 * |------------------|-----------------------------|
 * | 0x3040 – 0x309F  | Hiragana                    |
 * | 0x30A0 – 0x30FF  | Katakana                    |
 * | 0x4E00 – 0x9FFF  | CJK Unified Ideographs      |
 * | 0x3400 – 0x4DBF  | CJK Unified Ideographs Ext-A|
 * | 0xFF00 – 0xFFEF  | Halfwidth/Fullwidth Forms   |
 * | 0x3000 – 0x303F  | CJK Symbols and Punctuation |
 *
 * 注意:
 * - 空文字列は `false`。`codePointAt` が `undefined` を返す防御も兼ねる。
 * - サロゲートペア先頭の 1 コード単位ではなく **コードポイント**で判定する
 *   ため `codePointAt(0)` を使用（spec §5.4.2 L543）。ただし Ext-B 以降
 *   （U+20000..）は本表の対象外。実用上、Ext-B の使用は想定しない。
 * - 判定は 1 文字単位。String Iterator（`for (const c of str)`）で回すと
 *   サロゲートペアも 1 要素として扱えるため、呼び出し側はこれに従う。
 *
 * @param c 1 文字分の文字列（空文字もしくはサロゲートペア含む 1 grapheme）。
 * @returns CJK ブロックのいずれかに含まれるなら `true`。
 */
export function isCJK(c: string): boolean {
  if (!c) return false;
  const code = c.codePointAt(0);
  if (code === undefined) return false;
  return (
    (code >= 0x3040 && code <= 0x309f) || // Hiragana
    (code >= 0x30a0 && code <= 0x30ff) || // Katakana
    (code >= 0x4e00 && code <= 0x9fff) || // CJK Unified Ideographs
    (code >= 0x3400 && code <= 0x4dbf) || // CJK Unified Ideographs Ext-A
    (code >= 0xff00 && code <= 0xffef) || // Halfwidth/Fullwidth Forms
    (code >= 0x3000 && code <= 0x303f) //   CJK Symbols and Punctuation
  );
}

/**
 * Canvas `ctx.font` shorthand 文字列を構築する（spec §5.4.2 L552–554）。
 *
 * 書式: `` `${weight} ${fontSize}px ${family}` ``。
 * 例: `buildFontSpec('"Noto Sans JP", sans-serif', 24)` →
 *     `'500 24px "Noto Sans JP", sans-serif'`。
 *
 * `weight` のデフォルト `500` は spec §5.4.2 L552 で固定。本関数は weight
 * しか取らないが、本プロジェクトでは style / stretch を指定する必要がない
 * ため十分（spec §5.4.2 は weight + size + family のみで構成）。
 *
 * font-family 文字列の引用（`"Noto Sans JP"` など）は呼び出し側の責務。
 * 本関数はエスケープや引用を一切加えない。
 *
 * @param family CSS font-family 文字列（カンマ区切り可、引用は呼び出し側で）。
 * @param fontSize フォントサイズ（px、正の数）。
 * @param weight font-weight（整数、省略時 `500`）。
 * @returns Canvas `ctx.font` に代入可能な shorthand 文字列。
 */
export function buildFontSpec(family: string, fontSize: number, weight = 500): string {
  return `${weight} ${fontSize}px ${family}`;
}
